import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Input } from '~/components/ui/inputs/Input';
import { useCreateTenantAdmin } from '~/api/organizations';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';

interface AddTenantAdminModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}

export default function AddTenantAdminModal({
  open,
  onClose,
  organizationId,
}: AddTenantAdminModalProps) {
  const { t } = useTranslation('organizations');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [prevOpen, setPrevOpen] = useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setFirstName('');
      setLastName('');
      setEmail('');
    }
  }

  const createTenantAdmin = useCreateTenantAdmin();

  const isValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0;

  function handleFieldChange(fieldName: string, value: string) {
    if (fieldName === 'firstName') setFirstName(value);
    if (fieldName === 'lastName') setLastName(value);
    if (fieldName === 'email') setEmail(value);
  }

  async function handleSubmit() {
    if (!isValid) return;
    try {
      await createTenantAdmin.mutateAsync({
        input: {
          organizationId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
        },
      });
      toast.success(t('organizations.admin.success'));
      onClose();
    } catch (error) {
      toast.error(
        extractGraphQLErrorMessages(error as ClientError).join(', ') ||
          t('organizations.admin.error')
      );
    }
  }

  return (
    <Modal isOpen={open} onClose={onClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-[28rem] max-w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-prim-100 rounded-lg">
            <UserPlus size={20} className="text-prim" />
          </div>
          <div>
            <h2
              id="Modal-title"
              className="text-lg font-semibold text-neut-prim"
            >
              {t('organizations.admin.addTitle')}
            </h2>
            <p className="text-xs text-neut-sec mt-0.5">
              {t('organizations.admin.addSubtitle')}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            label={t('organizations.admin.firstName')}
            name="firstName"
            value={firstName}
            onChange={handleFieldChange}
            placeholder={t('organizations.admin.firstNamePlaceholder')}
          />
          <Input
            label={t('organizations.admin.lastName')}
            name="lastName"
            value={lastName}
            onChange={handleFieldChange}
            placeholder={t('organizations.admin.lastNamePlaceholder')}
          />
          <Input
            label={t('organizations.admin.email')}
            name="email"
            value={email}
            onChange={handleFieldChange}
            placeholder={t('organizations.admin.emailPlaceholder')}
          />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={createTenantAdmin.isPending}
              className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors disabled:opacity-50"
            >
              {t('organizations.admin.cancel')}
            </button>
            <button
              type="button"
              disabled={!isValid || createTenantAdmin.isPending}
              onClick={handleSubmit}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-prim rounded-lg hover:bg-prim-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createTenantAdmin.isPending
                ? t('organizations.admin.submitting')
                : t('organizations.admin.submit')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
